import {
	Grid,
	Typography,
	type TypographyProps,
	useTheme,
} from "@mui/material";
import { ArrowLeft } from "lucide-react";
import {
	getRoute,
	type RouteObject,
} from "web_myplann/Infrastructure/Utils/UrlUtils";
import { HeaderBase } from "web_myplann/Shared/Header/HeaderBase";
import { HeaderIconButton } from "web_myplann/Shared/Header/HeaderIconButton";
import { useNavigationHeaderTitleTypographyProps } from "web_myplann/Shared/Header/useNavigationHeaderTitleTypographyProps";
import { useNavigateBackOrDefault } from "web_myplann/Shared/Hooks/useNavigateBackOrDefault";

type Props<TRoute extends any[] = any[]> = {
	defaultRoute?: RouteObject<TRoute> | string;
	title: string | React.ReactNode;
	titleVariant?: TypographyProps["variant"];
	endComponent?: React.ReactNode;
	startComponent?: React.ReactNode;
	disablePadding?: boolean;
	bottomComponent?: React.ReactNode;
	disableBottomPadding?: boolean;
	alwaysUseDefaultRoute?: boolean;
	bgColor?: string;
	backIconColor?: string;
	backIconSize?: number;
	replaceOnBack?: boolean;
	backState?: unknown;
	onBack?: () => void;
};

const NavigationHeader = <TRoute extends any[] = any[]>({
	defaultRoute,
	title,
	titleVariant,
	endComponent,
	disablePadding,
	startComponent,
	bottomComponent,
	disableBottomPadding,
	alwaysUseDefaultRoute = false,
	bgColor,
	backIconColor,
	backIconSize,
	replaceOnBack = false,
	backState,
	onBack,
}: Props<TRoute>) => {
	const theme = useTheme();
	const headerProps = useNavigationHeaderTitleTypographyProps();

	const resolvedDefaultRoute = defaultRoute
		? typeof defaultRoute === "string"
			? defaultRoute
			: getRoute(defaultRoute)
		: undefined;
	const navigateBack = useNavigateBackOrDefault(resolvedDefaultRoute, {
		alwaysUseDefaultRoute,
		replaceOnBack,
		backState,
	});

	return (
		<HeaderBase
			disablePadding={disablePadding}
			bottomComponent={bottomComponent}
			disableBottomPadding={disableBottomPadding}
			bgColor={bgColor}
		>
			<Grid
				size={{ xs: 2 }}
				flexDirection="row"
				gap={1.2}
				display="flex"
				alignItems="center"
				component="div"
			>
				{(defaultRoute || onBack) && (
					<HeaderIconButton onClick={onBack ?? navigateBack}>
						<ArrowLeft
							color={backIconColor ?? theme.navigation.header.backIconColor}
							size={backIconSize}
						/>
					</HeaderIconButton>
				)}
				{startComponent}
			</Grid>

			<Grid
				size={{ xs: 8 }}
				display="flex"
				alignItems="center"
				justifyContent="center"
				textAlign="center"
				component="div"
			>
				{typeof title === "string" ? (
					<Typography
						{...(titleVariant
							? { variant: titleVariant, noWrap: true }
							: headerProps)}
					>
						{title}
					</Typography>
				) : (
					title
				)}
			</Grid>

			<Grid
				size={{ xs: 2 }}
				display="flex"
				justifyContent="flex-end"
				alignItems="center"
				gap={1.2}
				component="div"
			>
				{endComponent}
			</Grid>
		</HeaderBase>
	);
};

export default NavigationHeader;
